import { type MutableRefObject, useCallback, useMemo, useRef } from 'react';

type ScrollToElementReturnVal<T> = [() => void, MutableRefObject<T | null>];

/**
 * Hook used for scrolling to an element on the page.
 *
 * @example
 * const [executeScroll, ref] = useScrollToElement<HTMLHeadingElement>({ behavior: "smooth" })
 * useEffect(executeScroll, [])
 * return <div ref={ref}>Scroll to this element</div>
 */
export const useScrollToElement = <T>(
  scrollOptions?: boolean | ScrollIntoViewOptions | undefined,
): ScrollToElementReturnVal<T> => {
  const elementRef = useRef<null | T>(null);
  const defaultScrollOptions: ScrollIntoViewOptions = useMemo(
    () => ({ behavior: 'smooth', block: 'start', inline: 'nearest' }),
    [],
  );
  const executeScroll = useCallback(() => {
    (elementRef?.current as HTMLElement)?.scrollIntoView(
      scrollOptions !== undefined ? scrollOptions : defaultScrollOptions,
    );
  }, [defaultScrollOptions, scrollOptions]);

  return [executeScroll, elementRef];
};
